import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, BASE_URL } from '../utils/apiClient';
import { Shield, Key, AlertTriangle, CheckCircle, Info, SmartphoneNfc, User, Upload, Calendar, Clock, Briefcase, ChevronRight, ChevronDown, ChevronUp, ChevronLeft, CalendarDays } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import InfoModal from '../components/common/InfoModal';

const UserProfilePage = () => {
    const { username, handleLogout, mustChangePassword, twoFactorEnabled, hasPermission } = useAuth();
    
    // Profil / Dashboard State
    const [profileData, setProfileData] = useState(null);
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const fileInputRef = useRef(null);

    // Mevcut Güvenlik State'leri
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    const [mfaSetupData, setMfaSetupData] = useState(null);
    const [mfaCode, setMfaCode] = useState('');
    const [showMfaSetup, setShowMfaSetup] = useState(false);
    const [mfaError, setMfaError] = useState(null);
    const [mfaMessage, setMfaMessage] = useState(null);
    const [showDisableModal, setShowDisableModal] = useState(false);

    // Ay / Yıl State
    const currentDate = new Date();
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
    const [isSecurityOpen, setIsSecurityOpen] = useState(false);

    useEffect(() => {
        fetchDashboard(selectedYear, selectedMonth);
    }, [selectedYear, selectedMonth]);

    const fetchDashboard = async (y, m) => {
        try {
            setPageLoading(true);
            setError(null);
            const url = y && m ? `Profile/me-dashboard?year=${y}&month=${m}` : 'Profile/me-dashboard';
            const res = await apiClient(url);
            setProfileData(res);
            if (res?.profile?.hasAvatar && !avatarUrl) {
                await fetchAvatar();
            }
        } catch (err) {
            console.error("Dashboard verisi alınamadı:", err);
            setError(err?.payload?.message || err?.message || "Profil verisi yüklenemedi. Hesabınız bir çalışan kaydıyla eşleşmiyor olabilir.");
        } finally {
            setPageLoading(false);
        }
    };

    const fetchAvatar = async () => {
        try {
            // Version parameter to break cache
            const v = new Date().getTime();
            const res = await apiClient(`Profile/my-avatar?v=${v}`, { responseType: 'blob' });
            if (res.blob) {
                const url = URL.createObjectURL(res.blob);
                setAvatarUrl(url);
            }
        } catch(err) {
            console.error("Avatar yüklenemedi", err);
        }
    };

    const handleAvatarChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            alert('Dosya boyutu 5 MB limitini aşıyor.');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setUploadingAvatar(true);
        try {
            const token = localStorage.getItem('authToken');
            const firmCode = localStorage.getItem('firmCode');

            const res = await fetch(`${BASE_URL}/Profile/my-avatar`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Firm-Code': firmCode
                },
                body: formData
            });

            if (res.ok) {
                await fetchAvatar();
            } else {
                const errText = await res.text();
                alert(`Yükleme başarısız: ${errText}`);
            }
        } catch(err) {
            console.error(err);
            alert("Sunucuya bağlanırken hata oluştu.");
        } finally {
            setUploadingAvatar(false);
            if(fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const triggerFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Güvenlik Form Handlers
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setMessage(null);

        if (newPassword !== confirmPassword) {
            setError('Yeni şifreler eşleşmiyor.');
            return;
        }

        if (newPassword.length < 12) {
            setError('Yeni şifre en az 12 karakter olmalıdır.');
            return;
        }

        setLoading(true);
        try {
            const response = await apiClient('Auth/change-password', {
                method: 'POST',
                body: { currentPassword, newPassword }
            });

            setMessage(response.message || 'Şifreniz başarıyla değiştirildi. Güvenliğiniz için tüm oturumlarınız kapatıldı.');
            setTimeout(() => handleLogout(true), 3000);
        } catch (err) {
            setError(err.message || 'Şifre değiştirilirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const handleSetupMfa = async () => {
        setMfaError(null);
        try {
            setLoading(true);
            const response = await apiClient('Auth/mfa/setup', { method: 'POST', body: {} });
            setMfaSetupData(response);
            setShowMfaSetup(true);
        } catch (err) {
            setMfaError(err.payload?.message || err.message || 'MFA kurulum bilgileri alınamadı.');
        } finally {
            setLoading(false);
        }
    };

    const handleEnableMfa = async () => {
        setMfaError(null);
        setMfaMessage(null);
        if (mfaCode.length < 6) {
            setMfaError('Lütfen 6 haneli kodu girin.');
            return;
        }

        try {
            setLoading(true);
            await apiClient('Auth/mfa/enable', { method: 'POST', body: { code: mfaCode } });
            setMfaMessage('İki adımlı doğrulama (MFA) başarıyla aktifleştirildi. Yönlendiriliyorsunuz...');
            setShowMfaSetup(false);
            setMfaSetupData(null);
            setTimeout(() => handleLogout(true), 2000);
        } catch (err) {
            setMfaError(err.payload?.message || err.message || 'Doğrulama başarısız.');
        } finally {
            setLoading(false);
        }
    };

    const handleDisableMfaConfirm = async () => {
        setShowDisableModal(false);
        setMfaError(null);
        setMfaMessage(null);
        try {
            setLoading(true);
            await apiClient('Auth/mfa/disable', { method: 'POST' });
            setMfaMessage('İki adımlı doğrulama başarıyla kapatıldı. Yönlendiriliyorsunuz...');
            setTimeout(() => handleLogout(true), 2000);
        } catch (err) {
            setMfaError(err.payload?.message || err.message || 'MFA devre dışı bırakılamadı.');
        } finally {
            setLoading(false);
        }
    };

    if (pageLoading && !profileData) {
        return (
            <div className="flex items-center justify-center min-h-[50vh]">
                <div className="animate-spin text-blue-500 w-10 h-10 border-4 border-current border-t-transparent rounded-full" />
            </div>
        );
    }

    if (error && !profileData) {
        return (
            <div className="flex items-center justify-center min-h-[50vh] p-4 text-center">
                 <div className="bg-red-50 text-red-600 p-6 rounded-2xl shadow-sm border border-red-100 max-w-lg w-full">
                     <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500"/>
                     <h2 className="text-xl font-bold mb-2">Görüntüleme Hatası</h2>
                     <p>{error}</p>
                 </div>
            </div>
        );
    }

    const handlePrevMonth = () => {
        let newMonth = selectedMonth - 1;
        let newYear = selectedYear;
        if (newMonth < 1) {
            newMonth = 12;
            newYear -= 1;
        }
        setSelectedMonth(newMonth);
        setSelectedYear(newYear);
    };

    const handleNextMonth = () => {
        let newMonth = selectedMonth + 1;
        let newYear = selectedYear;
        if (newMonth > 12) {
            newMonth = 1;
            newYear += 1;
        }
        setSelectedMonth(newMonth);
        setSelectedYear(newYear);
    };

    const isNextMonthDisabled = () => {
        const now = new Date();
        return selectedYear > now.getFullYear() || (selectedYear === now.getFullYear() && selectedMonth >= now.getMonth() + 1);
    };

    const cp = profileData?.profile;
    const leave = profileData?.leaveSummary;
    const attendance = profileData?.attendanceSummary;
    const canUpdateAvatar = hasPermission('profile:update_avatar');

    const formatMinutes = (totalMinutes) => {
        if (!totalMinutes || totalMinutes === 0) return '0 dk';
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        if (hours > 0 && minutes > 0) return `${hours} sa ${minutes} dk`;
        if (hours > 0) return `${hours} sa`;
        return `${minutes} dk`;
    };

    return (
        <div className="w-full flex justify-center py-8 px-4">
            <div className="w-full max-w-7xl space-y-8">
                
                {/* --- HEADER --- */}
                <div className="flex items-center space-x-4 mb-2">
                    <div className="p-3 bg-blue-100 dark:bg-blue-900/40 rounded-xl">
                        <User className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">Profilim</h1>
                        <p className="text-gray-500 dark:text-gray-400">Özet bilgilerinizi ve güvenlik ayarlarınızı yönetin.</p>
                    </div>
                </div>

                {/* --- DASHBOARD GRIDS --- */}
                {cp && (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        
                        {/* LEFT COLUMN (PROFILE CARD) */}
                        <div className="lg:col-span-3 bg-white dark:bg-gray-800 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden relative group flex flex-col">
                            <div className="h-32 bg-gradient-to-r from-blue-500 to-indigo-600"></div>
                            <div className="px-6 pb-6 relative flex flex-col items-center text-center flex-1">
                                <div className="absolute -top-16">
                                    <div className="relative">
                                        {avatarUrl ? (
                                            <img src={avatarUrl} alt="Avatar" className="w-32 h-32 object-cover rounded-full border-4 border-white dark:border-gray-800 bg-white shadow-md" />
                                        ) : (
                                            <div className="w-32 h-32 rounded-full border-4 border-white dark:border-gray-800 bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-400 shadow-md">
                                                <User className="w-12 h-12" />
                                            </div>
                                        )}

                                        {canUpdateAvatar && (
                                            <>
                                                <button 
                                                    onClick={triggerFileInput} 
                                                    disabled={uploadingAvatar}
                                                    className="absolute bottom-1 right-1 p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 shadow-lg transform transition-all active:scale-95 border-2 border-white dark:border-gray-800"
                                                    title="Fotoğrafı Değiştir"
                                                >
                                                    <Upload className="w-4 h-4" />
                                                </button>
                                                <input 
                                                    type="file" 
                                                    ref={fileInputRef}
                                                    accept="image/jpeg,image/png,image/webp" 
                                                    className="hidden" 
                                                    onChange={handleAvatarChange}
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-20 space-y-1 w-full relative z-10">
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{cp.firstName} {cp.lastName}</h2>
                                    <p className="text-blue-600 dark:text-blue-400 font-medium text-sm flex items-center justify-center gap-1">
                                        <Briefcase className="w-4 h-4" /> {cp.positionName}
                                    </p>
                                    <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">{cp.departmentName}</p>
                                    
                                    <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700 w-full flex flex-col gap-4 text-left">
                                        <div className="flex items-center text-sm p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                            <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm mr-3">
                                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                                            </div>
                                            <div className="flex flex-col overflow-hidden">
                                                <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">E-posta</span>
                                                <span className="text-gray-900 dark:text-white font-semibold truncate" title={cp.email}>{cp.email}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center text-sm p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                            <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm mr-3">
                                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">Telefon</span>
                                                <span className="text-gray-900 dark:text-white font-semibold">{cp.phoneNumber || '-'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* LEAVE BALANCES CHIPS */}
                                    <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700 w-full text-left">
                                        <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-1.5">
                                            <CalendarDays className="w-4 h-4 text-indigo-400" /> İzin Bakiyelerim
                                        </h4>
                                        {profileData?.leaveSummary?.balances?.length > 0 ? (
                                            <div className="flex flex-wrap gap-2">
                                                {profileData.leaveSummary.balances.map((b, idx) => {
                                                    let displayVal = `${b.remainingValue} ${b.unit}`;
                                                    if (b.unit === 'Dakika') {
                                                        const hrs = Math.floor(b.remainingValue / 60);
                                                        const mins = b.remainingValue % 60;
                                                        displayVal = hrs > 0 
                                                            ? (mins > 0 ? `${hrs} sa ${mins} dk` : `${hrs} sa`) 
                                                            : `${mins} dk`;
                                                    }
                                                    return (
                                                        <span key={idx} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/30 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-medium shadow-sm transition-transform hover:-translate-y-0.5">
                                                            <span>{b.leaveTypeName}</span>
                                                            <span className="opacity-40">·</span>
                                                            <span className="font-bold">{displayVal}</span>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 italic bg-gray-50 dark:bg-gray-700/30 px-3 py-2 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 w-fit">
                                                Bakiyeli izniniz bulunmamaktadır.
                                            </p>
                                        )}


                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT COLUMN (STATISTICS & OVERVIEWS) */}
                        <div className="lg:col-span-9 flex flex-col space-y-8">
                            
                            {/* --- TOP ROW: ATTENDANCE BASIC STATS --- */}
                            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 flex flex-col hover:shadow-xl transition-shadow w-full">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                                        <h3 className="text-xl font-bold flex items-center gap-2 text-gray-900 dark:text-white">
                                            <Clock className="w-6 h-6 text-rose-500" />
                                            Aylık Çalışma Özeti
                                        </h3>
                                        <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-1 border border-gray-100 dark:border-gray-600 self-start sm:self-auto shadow-sm">
                                            <button onClick={handlePrevMonth} className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors" title="Önceki Ay">
                                                <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                                            </button>
                                            <span className="text-sm font-bold w-28 text-center select-none text-gray-800 dark:text-gray-200 uppercase tracking-wide">
                                                {attendance?.periodLabel || `${selectedMonth}/${selectedYear}`}
                                            </span>
                                            <button onClick={handleNextMonth} disabled={isNextMonthDisabled()} className={`p-1.5 transition-colors rounded ${isNextMonthDisabled() ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-200 dark:hover:bg-gray-600'}`} title="Sonraki Ay">
                                                <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* INNER GRIDS */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
                                        <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl border border-gray-100 border-opacity-50 dark:border-none shadow-sm h-full">
                                            <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">Fiili Çalışılan Gün</span>
                                            <span className="text-xl font-black text-gray-800 dark:text-gray-100">{attendance?.workedDaysCount || 0} Gün</span>
                                        </div>
                                        <div className="flex justify-between items-center p-4 bg-rose-50 dark:bg-rose-900/10 rounded-2xl border border-rose-100 border-opacity-50 dark:border-none shadow-sm h-full">
                                            <span className="text-sm font-semibold text-rose-700 dark:text-rose-300">Devamsız Gün</span>
                                            <span className="text-xl font-black text-rose-600 dark:text-rose-400">{attendance?.missingDaysCount || 0} <span className="text-base text-rose-400/70 font-bold ml-1">Gün</span></span>
                                        </div>
                                        <div className="flex justify-between items-center p-4 bg-orange-50 dark:bg-orange-900/10 rounded-2xl border border-orange-100 border-opacity-50 dark:border-none shadow-sm h-full">
                                            <span className="text-sm font-semibold text-orange-700 dark:text-orange-300">Geç Girişler</span>
                                            <span className="text-xl font-black text-orange-500 dark:text-orange-400">{attendance?.lateArrivalsCount || 0} <span className="text-base text-orange-400/70 font-bold ml-1">Kez</span></span>
                                        </div>
                                        <div className="flex justify-between items-center p-4 bg-amber-50 dark:bg-amber-900/10 rounded-2xl border border-amber-100 border-opacity-50 dark:border-none shadow-sm h-full">
                                            <span className="text-sm font-semibold text-amber-700 dark:text-amber-300">Erken Çıkışlar</span>
                                            <span className="text-xl font-black text-amber-500 dark:text-amber-400">{attendance?.earlyExitsCount || 0} <span className="text-base text-amber-400/70 font-bold ml-1">Kez</span></span>
                                        </div>
                                        <div className="flex justify-between items-center p-4 bg-red-50 dark:bg-red-900/10 rounded-2xl border border-red-100 border-opacity-50 dark:border-none shadow-sm h-full">
                                            <span className="text-sm font-semibold text-red-700 dark:text-red-300">Eksik Süre</span>
                                            <span className="text-xl font-black text-red-600 dark:text-red-400">{formatMinutes(attendance?.totalUndertimeMinutes)}</span>
                                        </div>
                                        <div className="flex justify-between items-center p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl border border-emerald-100 border-opacity-50 dark:border-none shadow-sm h-full">
                                            <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">Fazla Mesai</span>
                                            <span className="text-xl font-black text-emerald-600 dark:text-emerald-400">{formatMinutes(attendance?.totalOvertimeMinutes)}</span>
                                        </div>
                                    </div>
                                </div>

                            {/* --- BOTTOM ROW: SECONDARY DATA (Logs & Upcoming Leaves) --- */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* RECENT ATTENDANCE LOGS (Col 1-2) */}
                                <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 flex flex-col hover:shadow-xl transition-shadow lg:col-span-2">
                                    <div className="flex items-center justify-between mb-4 border-b border-gray-100 dark:border-gray-700 pb-4">
                                        <h3 className="text-lg font-bold flex items-center gap-2 text-gray-900 dark:text-white">
                                            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path></svg>
                                            Son 5 Puantaj Kaydı
                                        </h3>
                                        <span className="text-xs font-semibold text-blue-600 bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full uppercase">
                                            {attendance?.periodLabel || `${selectedMonth}/${selectedYear}`}
                                        </span>
                                    </div>
                                    
                                    {attendance?.recentLogs?.length > 0 ? (
                                        <div className="flex-1">
                                            <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-700">
                                                <table className="min-w-full text-sm text-left">
                                                    <thead className="bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-300 font-semibold whitespace-nowrap">
                                                        <tr>
                                                            <th className="px-4 py-3">Tarih</th>
                                                            <th className="px-4 py-3">Durum</th>
                                                            <th className="px-4 py-3 text-center">Giriş</th>
                                                            <th className="px-4 py-3 text-center">Çıkış</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                                        {attendance.recentLogs.map((log, index) => (
                                                            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                                                                <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-200">
                                                                    {new Date(log.date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })}
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                                                                        log.status === "Gelmedi" || log.status?.includes("Devamsız") ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400" :
                                                                        log.status === "Tatil" || log.status === "İzinli" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" :
                                                                        "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                                                    }`}>
                                                                        {log.status}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400 font-mono">{log.firstEntry}</td>
                                                                <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400 font-mono">{log.lastExit}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex-1 flex flex-col items-center justify-center py-8 text-gray-400 opacity-80">
                                            <svg className="w-12 h-12 mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                            <p className="font-medium">Bu aya ait puantaj kaydı bulunmuyor.</p>
                                        </div>
                                    )}
                                </div>

                                {/* UPCOMING LEAVES (Col 3) */}
                                <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 flex flex-col hover:shadow-xl transition-shadow lg:col-span-1">
                                    <div className="flex items-center justify-between mb-4 border-b border-gray-100 dark:border-gray-700 pb-4">
                                        <h3 className="text-lg font-bold flex items-center gap-2 text-gray-900 dark:text-white">
                                            <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>
                                            Yaklaşan İzinler
                                        </h3>
                                    </div>
                                    
                                    {leave?.upcomingLeaves?.length > 0 ? (
                                        <div className="space-y-3 flex-1">
                                            {leave.upcomingLeaves.slice(0, 3).map((l) => (
                                                <div key={l.id} className="flex flex-col text-sm bg-indigo-50 dark:bg-indigo-900/10 p-3 rounded-xl border border-indigo-100 dark:border-indigo-800/30">
                                                    <span className="font-bold text-indigo-900 dark:text-indigo-200">{l.leaveTypeName}</span>
                                                    <span className="text-indigo-600 dark:text-indigo-400 text-xs mt-1 font-medium flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        {new Date(l.startDate).toLocaleDateString('tr-TR')} - {new Date(l.endDate).toLocaleDateString('tr-TR')}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="flex-1 flex flex-col items-center justify-center text-center py-6 opacity-80">
                                            <div className="w-16 h-16 bg-gray-50 dark:bg-gray-700/50 rounded-full flex items-center justify-center mb-4">
                                                <CalendarDays className="w-8 h-8 text-gray-300 dark:text-gray-500" />
                                            </div>
                                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Yakın tarihli planlanmış<br/>izin kaydınız yok.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- FULL WIDTH LEAVE PROGRESS BARS --- */}
                {profileData?.leaveSummary?.balances?.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 md:p-8 mt-8 transition-shadow hover:shadow-xl w-full">
                        <div className="flex items-center gap-4 mb-8 border-b border-gray-100 dark:border-gray-700 pb-6">
                            <div className="p-3 bg-indigo-50 dark:bg-indigo-500/10 rounded-2xl shadow-sm">
                                <CalendarDays className="w-7 h-7 text-indigo-500 dark:text-indigo-400" />
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold flex items-center gap-2 text-gray-900 dark:text-white tracking-tight">
                                    İzin Bakiyelerim ve Hakediş Durumu
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">Sistem üzerindeki güncel izin haklarınız ve bir sonraki hakedişinize kalan süreler</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-2">
                            {profileData.leaveSummary.balances.map((b, idx) => {
                                const remVal = b.remainingValue || 0;
                                const totVal = b.totalValue !== undefined ? b.totalValue : b.remainingValue || 0;
                                const usVal = b.usedValue !== undefined ? b.usedValue : 0;
                                
                                let displayVal = `${remVal} ${b.unit}`;
                                let totalDisplayVal = `${totVal} ${b.unit}`;
                                let usedDisplayVal = `${usVal} ${b.unit}`;
                                
                                if (b.unit === 'Dakika') {
                                    const formatMins = (val) => {
                                        const h = Math.floor(val/60);
                                        const m = val % 60;
                                        return h > 0 ? (m > 0 ? `${h}sa ${m}dk` : `${h}sa`) : `${m}dk`;
                                    };
                                    displayVal = formatMins(remVal);
                                    totalDisplayVal = formatMins(totVal);
                                    usedDisplayVal = formatMins(usVal);
                                }

                                const percentage = totVal > 0 ? Math.min(100, Math.max(0, (remVal / totVal) * 100)) : 100;

                                return (
                                    <div key={`prog-${idx}`} className="flex flex-col p-6 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-700/30 rounded-2xl border border-gray-100 dark:border-gray-700/50 hover:border-indigo-200 dark:hover:border-indigo-500/50 transition-all hover:-translate-y-1 hover:shadow-lg lg:col-span-1">
                                        <div className="flex justify-between items-start mb-6">
                                            <div className="pr-2">
                                                <h4 className="text-base font-bold text-gray-800 dark:text-gray-200 mb-1.5 line-clamp-1" title={b.leaveTypeName}>{b.leaveTypeName}</h4>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Top. Hakediş: <span className="font-bold text-gray-700 dark:text-gray-300">{totalDisplayVal}</span></p>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <span className="block text-[28px] lg:text-[32px] font-black text-indigo-600 dark:text-indigo-400 leading-none mb-1 tracking-tight">{displayVal}</span>
                                                <span className="text-[10px] font-bold text-indigo-400/80 dark:text-indigo-400/60 uppercase tracking-[0.2em]">Kalan</span>
                                            </div>
                                        </div>

                                        {/* Progress Bar Track */}
                                        <div className="h-2.5 w-full bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden flex shadow-inner mb-3">
                                            <div 
                                                className={`h-full rounded-full transition-all duration-1000 ease-out bg-gradient-to-r ${percentage > 30 ? 'from-indigo-400 to-indigo-500 dark:from-indigo-500 dark:to-indigo-400 shadow-[0_0_10px_rgba(99,102,241,0.4)]' : 'from-rose-400 to-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.4)]'}`}
                                                style={{ width: `${percentage}%` }}
                                            ></div>
                                        </div>
                                        
                                        <div className="flex justify-between items-center text-xs font-semibold text-gray-500 dark:text-gray-400">
                                            <span>Kullanılan: <span className="text-gray-700 dark:text-gray-300 font-bold">{usedDisplayVal}</span></span>
                                            <span className={percentage <= 30 ? 'text-rose-500 dark:text-rose-400' : 'text-indigo-600 dark:text-indigo-400'}>
                                                {percentage.toFixed(0)}% Kaldı
                                            </span>
                                        </div>

                                        {/* Dynamic Next Entitlement Meta Box */}
                                        {b.nextEntitlementDays !== undefined && b.nextEntitlementDays !== null && (
                                            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700/50 flex flex-col gap-1.5">
                                                <div className="flex items-center justify-between text-[11px] font-medium text-gray-500 dark:text-gray-400">
                                                    <span className="flex items-center gap-1.5">
                                                        <Info className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
                                                        Sonraki Hakediş:
                                                    </span>
                                                    <span className="text-indigo-600 dark:text-indigo-400 font-bold">{b.nextEntitlementDays} Gün Sonra</span>
                                                </div>
                                                <p className="text-[10.5px] text-gray-400 dark:text-gray-500 leading-snug">
                                                    Kıdem ve yaş kuralları değerlendirilerek tahmini olarak <span className="font-bold text-gray-600 dark:text-gray-300">{b.nextEntitlementAmount} {b.unit}</span> eklenecektir.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* --- SECURITY SETTINGS --- */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700 mt-8 transition-all duration-300">
                    <button 
                        onClick={() => setIsSecurityOpen(!isSecurityOpen)}
                        className="w-full text-left bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-4 flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-slate-500"
                    >
                        <div className="flex items-center space-x-3">
                            <Shield className="w-6 h-6 text-slate-200" />
                            <h2 className="text-xl font-bold text-white">Güvenlik Ayarları</h2>
                        </div>
                        {isSecurityOpen ? (
                            <ChevronUp className="w-6 h-6 text-slate-300" />
                        ) : (
                            <ChevronDown className="w-6 h-6 text-slate-300" />
                        )}
                    </button>

                    {isSecurityOpen && (
                        <div className="p-6 md:p-8 border-t border-slate-600">
                            {/* Parola Değiştir Formu */}
                        <div className="mb-8 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-xl flex items-start space-x-3">
                            <Info className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-blue-800 dark:text-blue-200">Güvenli Şifre Kriterleri</h3>
                                <ul className="text-sm text-blue-700 dark:text-blue-300 mt-1 list-disc list-inside">
                                    <li>En az 12 karakter uzunluğunda olmalıdır.</li>
                                    <li>Harf, rakam ve özel karakter kombinasyonu önerilir.</li>
                                </ul>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mevcut Şifre</label>
                                    <div className="relative">
                                        <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        <input
                                            type="password"
                                            required
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            placeholder="Mevcut şifrenizi girin"
                                            className="w-full pl-12 pr-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-slate-500 outline-none transition-all dark:text-white"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Yeni Şifre</label>
                                    <input
                                        type="password"
                                        required
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="En az 12 karakter"
                                        className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-slate-500 outline-none transition-all dark:text-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Yeni Şifre (Yeniden)</label>
                                    <input
                                        type="password"
                                        required
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Aynı şifreyi girin"
                                        className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-slate-500 outline-none transition-all dark:text-white"
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 flex items-center space-x-2">
                                    <AlertTriangle className="w-5 h-5" /><span>{error}</span>
                                </div>
                            )}
                            {message && (
                                <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-600 flex items-center space-x-2">
                                    <CheckCircle className="w-5 h-5" /><span>{message}</span>
                                </div>
                            )}

                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`px-8 py-3 rounded-xl text-white font-bold transition-all ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-slate-700 hover:bg-slate-800'}`}
                                >
                                    {loading ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
                                </button>
                            </div>
                        </form>

                        {/* MFA BAŞLANGICI */}
                        <div className="mt-12 pt-8 border-t border-gray-100 dark:border-gray-700">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                    <div className={`p-3 rounded-xl ${twoFactorEnabled ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                                        <SmartphoneNfc className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold dark:text-white">İki Adımlı Doğrulama (MFA)</h3>
                                        <p className="text-gray-500 text-sm">Hesabınıza ekstra bir güvenlik katmanı ekleyin.</p>
                                    </div>
                                </div>
                                <div className={`px-3 py-1 rounded-full text-xs font-bold ${twoFactorEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
                                    {twoFactorEnabled ? 'AÇIK' : 'KAPALI'}
                                </div>
                            </div>

                            {/* ... (MFA UI Mantığı, Mevcut ile Aynı) ... */}
                            {!twoFactorEnabled && !showMfaSetup && (
                                <div className="flex flex-col sm:flex-row items-center gap-4 p-4 mt-6 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                                    <p className="text-sm text-slate-600 dark:text-slate-400 flex-1">
                                        Aktivasyon sonrası sadece şifrenizle değil, mobil cihazınızdan alacağınız geçici bir kod ile de giriş yapacaksınız.
                                    </p>
                                    <button
                                        onClick={handleSetupMfa}
                                        disabled={loading}
                                        className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg"
                                    >
                                        MFA Aktifleştir
                                    </button>
                                </div>
                            )}

                            {mfaError && <div className="p-3 my-3 text-red-600 bg-red-50 rounded-lg">{mfaError}</div>}

                            {!twoFactorEnabled && showMfaSetup && mfaSetupData && (
                                <div className="mt-6 p-6 border rounded-xl bg-slate-50 dark:bg-slate-800 dark:border-slate-700 text-center">
                                    <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">Aşağıdaki barkodu Authenticator uygulamanızla okutun.</p>
                                    <div className="inline-block p-4 bg-white border rounded-xl mb-4 shadow"><QRCodeSVG value={mfaSetupData.otpauthUri} size={150} level='Q' /></div>
                                    <p className="font-mono text-sm mb-4 bg-gray-200 dark:bg-gray-700 p-2 rounded inline-block">{mfaSetupData.manualKey}</p>
                                    <div className="max-w-xs mx-auto flex gap-2">
                                        <input
                                            type="text"
                                            value={mfaCode}
                                            onChange={(e) => setMfaCode(e.target.value.replace(/[^0-9]/g, ''))}
                                            maxLength={6}
                                            placeholder="000000"
                                            className="w-full text-center tracking-widest font-mono text-lg p-2 border rounded-lg dark:bg-gray-900 dark:text-white"
                                        />
                                        <button onClick={handleEnableMfa} className="px-4 py-2 bg-emerald-600 text-white font-bold rounded-lg hover:bg-emerald-700">Doğrula</button>
                                    </div>
                                    <button onClick={() => setShowMfaSetup(false)} className="mt-4 text-sm text-gray-500 hover:underline">Vazgeç</button>
                                </div>
                            )}

                            {twoFactorEnabled && (
                                <div className="mt-6">
                                    <button onClick={() => setShowDisableModal(true)} className="px-6 py-2.5 text-rose-600 border border-rose-200 hover:bg-rose-50 rounded-lg font-medium transition">
                                        MFA'yı Kapat
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>

            <InfoModal
                isOpen={showDisableModal}
                onClose={() => setShowDisableModal(false)}
                title="MFA'yı Kapat"
                message="İki adımlı doğrulamayı devre dışı bırakmak istediğinize emin misiniz?"
                type="warning"
                onConfirm={handleDisableMfaConfirm}
                isLoading={loading}
            />
        </div>
    );
};

export default UserProfilePage;
